import { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { CompanyApi } from '../../../../../api/companyApi';
import { errorToast, successToast, getLocalStorageItem } from '../../../../../utils/helper';
import Loader from '../../../../../component/common/Loader';
import Topbar from '../../../../PulseSurvey/Topbar';
import Breadcrumb from '../../../../../component/common/Breadcrumb';
import AddDocumentFolderModal from '../components/AddDocumentFolderModal';
import newFolderIcon from '../../../../../assets/svg/icons/New-Folder--Streamline-Flex.svg';
import { EmptyState } from '../../../../../component/common/StandardUI';

const FOLDERS_LIMIT = 100;

const DocumentHub = () => {
  const navigate = useNavigate();
  const userData = JSON.parse(getLocalStorageItem('userData'));
  const slug = userData?.slug;
  const pages = [
    { name: 'People plan', href: `/${slug}/people-plan`, current: false },
    { name: 'Document Hub', href: `/${slug}/people-plan/document-hub`, current: true }
  ];

  const [folders, setFolders] = useState([]);
  const [loader, setLoader] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [creating, setCreating] = useState(false);

  const fetchFolders = useCallback(async () => {
    try {
      setLoader(true);
      const response = await CompanyApi.getAllDocumentFolders(1, FOLDERS_LIMIT);
      setFolders(response?.data?.meta?.code === 1 ? response?.data?.data || [] : []);
    } catch (error) {
      errorToast(error.message || 'Failed to load folders');
    } finally {
      setLoader(false);
    }
  }, []);

  useEffect(() => {
    fetchFolders();
  }, [fetchFolders]);

  const handleCreateFolder = async (name) => {
    try {
      setCreating(true);
      const response = await CompanyApi.createDocumentFolder({ name });
      if (response?.data?.meta?.code === 1) {
        successToast(response?.data?.meta?.message || 'Folder created successfully');
        setIsModalOpen(false);
        fetchFolders();
      } else {
        errorToast(response?.data?.meta?.message || 'Failed to create folder');
      }
    } catch (error) {
      errorToast(error.message || 'Failed to create folder');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="relative lg:mb-[45px]">
      {loader && <Loader />}
      <Helmet>
        <meta charSet="utf-8" />
        <title>Document hub</title>
      </Helmet>
      <Topbar title="Document hub" infoBtn={false} />
      <Breadcrumb pageList={pages} className="lg:mt-8" />

      <div className="mt-6">
        <div className="rounded-input border-[1.5px] border-shoorah-borderGray bg-white dark:bg-shoorah-darkBgTabColor dark:border-shoorah-darkBorder p-5 sm:p-6">
          <h3 className="text-base font-medium text-gray-700 dark:text-gray-200">Your folders</h3>
          <p className="text-sm text-gray-800 dark:text-gray-200 mt-1 mb-5">
            Track the financial and cultural impact of your people strategy in real time.
          </p>

          {!loader && folders.length === 0 ? (
            <EmptyState
              size="lg"
              title="No folders yet"
              description="Create a folder to start organising your Wellbeing Strategy, Policy Documents and other People Plan resources."
            />
          ) : (
            <div className="grid grid-cols-4 gap-4">
              {folders.map((folder) => (
                <button
                  key={folder._id}
                  type="button"
                  onClick={() => navigate(`/${slug}/people-plan/document-hub/${folder._id}`)}
                  className="flex h-[88px] w-full items-center gap-3 rounded-input border-[1.5px] border-shoorah-borderGray dark:border-shoorah-darkBorder p-4 text-left hover:border-shoorah-btn dark:hover:border-shoorah-btnDark transition-colors"
                >
                  <img src={newFolderIcon} alt="" className="w-6 h-6 shrink-0 dark:invert" />
                  <span className="min-w-0 text-sm text-gray-700 dark:text-gray-200 line-clamp-2 break-words">
                    {folder.name}
                  </span>
                </button>
              ))}
            </div>
          )}

          <button
            type="button"
            onClick={() => setIsModalOpen(true)}
            className="mt-5 text-sm font-medium text-shoorah-primary hover:underline"
          >
            Create new folder
          </button>
        </div>
      </div>

      <AddDocumentFolderModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleCreateFolder}
        submitting={creating}
      />
    </div>
  );
};

export default DocumentHub;
